import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {useCountriesCostYears} from './useCountriesCostYears';

interface UseCountryCostsQueryParamsResult {
  countryId: string | null;
  handleChangeCountryId: (countryId: string | null) => void;
  year: number | null;
  handleChangeYear: (year: number | null) => void;
}

export const useCountryCostsQueryParams =
  (): UseCountryCostsQueryParamsResult => {
    const {countriesCostYears} = useCountriesCostYears();

    const countryIdToYears = useMemo(
      () =>
        (countriesCostYears ?? []).reduce((map, {countryId, years}) => {
          map.set(countryId, years);
          return map;
        }, new Map<string, number[]>()),
      [countriesCostYears]
    );

    const [countryId, setCountryId] = useState<string | null>(null);
    const [year, setYear] = useState<number | null>(null);

    const location = useLocation();
    const navigate = useNavigate();

    const updateUrlParams = useCallback(
      (newCountryId: string | null, newYear: number | null) => {
        const params = new URLSearchParams(location.search);

        if (newCountryId) params.set('countryId', newCountryId);
        else params.delete('countryId');

        if (newYear != null) params.set('year', String(newYear));
        else params.delete('year');

        navigate(`?${params.toString()}`, {replace: false});
      },
      [location.search, navigate]
    );

    const handleChangeCountryId = useCallback(
      (newCountryId: string | null) => {
        // try to keep already selected year if it exists in the new country
        let newYear: number | null = null;
        if (
          newCountryId &&
          year &&
          countryIdToYears.get(newCountryId)?.includes(year)
        ) {
          newYear = year;
        }

        updateUrlParams(newCountryId, newYear);
      },
      [year, countryIdToYears, updateUrlParams]
    );

    const handleChangeYear = useCallback(
      (newYear: number | null) => {
        updateUrlParams(countryId, newYear);
      },
      [countryId, updateUrlParams]
    );

    // Sync URL -> state and reset invalid params
    useEffect(() => {
      if (!countryIdToYears.size) return;

      const params = new URLSearchParams(location.search);

      const countryId = params.get('countryId');
      if (countryId && !countryIdToYears.has(countryId)) {
        // reset both country and year
        navigate(`?`, {replace: true});
        return;
      }

      const year = params.has('year') ? Number(params.get('year')) : null;
      if (year) {
        if (!countryId || !countryIdToYears.get(countryId)?.includes(year)) {
          // reset year
          params.delete('year');
          navigate(`?${params.toString()}`, {replace: true});
          return;
        }
      }

      setCountryId(countryId);
      setYear(year);
    }, [countryIdToYears, location.search, navigate]);

    return {countryId, handleChangeCountryId, year, handleChangeYear};
  };
